import { useCallback, useEffect, useState } from 'react';
import { useApi } from 'api/api-context';
import BookingAccountLayout from 'pages/booking/account/BookingAccountLayout';
import { AccountError, AccountSkeleton } from 'pages/booking/account/account-utils';
import useLang from 'lang';
import { formatLocaleDateTime } from 'lang/date-time';
import AccountFilters from 'pages/booking/account/AccountFilters';
import { Link } from 'react-router-dom';
import { BOOKING_SLUG_CREDIT_PURCHASE_PATH } from 'routes/paths';

const ACTIVITY_LABELS: Record<string, string> = {
  grant: 'Credits granted',
  charge: 'Credits used',
  refund: 'Credits refunded',
  expiry: 'Credits expired',
  purchase: 'Credits purchased',
};

/**
 * Current non-zero balances followed by the derived credit ledger activity.
 */
export default function AccountCredits() {
  const { getAction } = useApi();
  const { _t, _tn, lang } = useLang();
  const [balances, setBalances] = useState<any[]>([]);
  const [filterOptions, setFilterOptions] = useState<{ buildings: any[]; spaces: any[] }>({
    buildings: [],
    spaces: [],
  });
  const [activity, setActivity] = useState<any[]>([]);
  const [spaces, setSpaces] = useState<any[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [filters, setFilters] = useState({ buildingId: '', spaceId: '', type: '', from: '', to: '' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(
    async (append = false) => {
      setLoading(true);
      setError(false);
      try {
        const [balanceData, activityData, spacesData] = (await Promise.all([
          getAction('GetMyReservationCredits')({}),
          getAction('GetMyReservationCreditActivity')({
            query: {
              limit: 20,
              ...Object.fromEntries(Object.entries(filters).filter(([, value]) => value)),
              ...(append && nextCursor ? { cursor: nextCursor } : {}),
            } as any,
          }),
          getAction('GetReservableSpaces')({}),
        ])) as any[];
        setBalances(balanceData.balances || []);
        setFilterOptions(balanceData.filterOptions || { buildings: [], spaces: [] });
        setSpaces((spacesData.spaces || []).filter((space: any) => space.creditProducts?.length));
        setActivity((current) =>
          append ? [...current, ...(activityData.activity || [])] : activityData.activity || []
        );
        setNextCursor(activityData.nextCursor || null);
      } catch (_error) {
        setError(true);
      } finally {
        setLoading(false);
      }
    },
    [filters, getAction, nextCursor]
  );

  useEffect(() => {
    setActivity([]);
    setNextCursor(null);
    void load();
  }, [filters]);

  return (
    <BookingAccountLayout>
      {loading && balances.length === 0 && <AccountSkeleton />}
      {error && <AccountError retry={() => void load()} />}
      <div className="account-credit-grid">
        {balances.map((balance) => (
          <article className="account-credit-card" key={balance.space.id}>
            <p>{balance.building.title}</p>
            <h2>{balance.space.name}</h2>
            <strong>{balance.available}</strong>
            <span>{_tn('available credit', 'available credits', Number(balance.available))}</span>
            <ul>
              {balance.expirations.map((expiration: any) => (
                <li key={expiration.expiresAt}>
                  {_tn('%d credit', '%d credits', Number(expiration.credits)).replace('%d', String(expiration.credits))}{' '}
                  · {_t('expires')} {formatLocaleDateTime(expiration.expiresAt, lang)}
                </li>
              ))}
            </ul>
          </article>
        ))}
      </div>
      {spaces.length > 0 && (
        <section className="account-credit-offers">
          <h2>{_t('Buy credits')}</h2>
          <p>{_t('Choose an accessible space to see its available credit bundles.')}</p>
          <div className="account-credit-grid">
            {spaces.map((space) => (
              <article className="account-credit-card" key={`offer-${space.id}`}>
                <h2>{space.name}</h2>
                <p>
                  {space.creditProducts
                    .map(
                      (product: any) =>
                        `${product.credits} / ${Number(product.priceWithVat).toFixed(2)} ${product.currencyCode}`
                    )
                    .join(' · ')}
                </p>
                <Link
                  className="account-button account-credit-buy-link"
                  to={BOOKING_SLUG_CREDIT_PURCHASE_PATH.insert({ slug: space.slug })}
                  state={{ space }}
                >
                  {_t('Buy credits')}
                </Link>
              </article>
            ))}
          </div>
        </section>
      )}
      {!loading && !error && balances.length === 0 && spaces.length === 0 && (
        <div className="account-state">
          <h2>{_t('No active credits')}</h2>
          <p>{_t('You currently have no credits available for booking.')}</p>
        </div>
      )}
      <div className="account-activity-heading">
        <h2>{_t('Credit activity')}</h2>
      </div>
      <AccountFilters activeFilterCount={Object.values(filters).filter(Boolean).length}>
        <select
          value={filters.buildingId}
          aria-label={_t('Building')}
          onChange={(event) => setFilters((current) => ({ ...current, buildingId: event.target.value }))}
        >
          <option value="">{_t('All buildings')}</option>
          {filterOptions.buildings.map((building) => (
            <option key={building.id} value={building.id}>
              {building.title}
            </option>
          ))}
        </select>
        <select
          value={filters.spaceId}
          aria-label={_t('Space')}
          onChange={(event) => setFilters((current) => ({ ...current, spaceId: event.target.value }))}
        >
          <option value="">{_t('All spaces')}</option>
          {filterOptions.spaces
            .filter((space) => !filters.buildingId || Number(space.buildingId) === Number(filters.buildingId))
            .map((space) => (
              <option key={space.id} value={space.id}>
                {space.name}
              </option>
            ))}
        </select>
        <select
          value={filters.type}
          aria-label={_t('Activity type')}
          onChange={(event) => setFilters((current) => ({ ...current, type: event.target.value }))}
        >
          <option value="">{_t('All activity')}</option>
          {Object.entries(ACTIVITY_LABELS).map(([value, label]) => (
            <option value={value} key={value}>
              {_t(label)}
            </option>
          ))}
        </select>
        <label className="account-filter-field">
          <span>{_t('From')}:</span>
          <input
            aria-label={_t('From')}
            type="date"
            value={filters.from}
            onChange={(event) => setFilters((current) => ({ ...current, from: event.target.value }))}
          />
        </label>
        <label className="account-filter-field">
          <span>{_t('To')}:</span>
          <input
            aria-label={_t('To')}
            type="date"
            value={filters.to}
            onChange={(event) => setFilters((current) => ({ ...current, to: event.target.value }))}
          />
        </label>
      </AccountFilters>
      <div className="account-activity-list">
        {activity.map((item) => (
          <article key={item.id}>
            <div>
              <strong>{_t(ACTIVITY_LABELS[item.type])}</strong>
              <p>
                {item.space.name} · {formatLocaleDateTime(item.occurredAt, lang)}
              </p>
            </div>
            <span className={item.amount >= 0 ? 'positive' : 'negative'}>
              {item.amount > 0 ? '+' : ''}
              {item.amount}
            </span>
          </article>
        ))}
      </div>
      {nextCursor && (
        <button className="account-button account-load-more" disabled={loading} onClick={() => void load(true)}>
          {loading ? _t('Loading...') : _t('Load more')}
        </button>
      )}
    </BookingAccountLayout>
  );
}
